import { Box, Button, Card, CardContent, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import React, { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { Add, AdminPanelSettingsOutlined, Done, EditOutlined } from '@mui/icons-material'
import { IconPicker } from '../display-elements-components/icon-picker'
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { useNavigate } from 'react-router-dom'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { ICreateReductionAction, IReductionActionContent } from '../../../shared/services/reduction-action/reduction-action.model'
import { ReductionActionContentSummary } from './reduction-action-content-summary'
import { AddCustomReductionActionDialog } from '../../dialogs/reduction/add-custom-reduction-action-dialog'
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked'

export const ReductionActionCard: FunctionComponent<{
  name: string,
  icon: IconName,
  relevance: number,
  savingEmission: number,
  hasAction: boolean,
  actionId: string,
  isCustom: boolean,
  description: string,
  categoryId: string[],
  groupId: string,
  setDialogOpen: React.Dispatch<SetStateAction<any>>,
  getAllActionsForOrganisation: () => void,
  getGroupActions: () => void,
  selectedGroup: string,
  getRecommendedActions: () => void,
  content?: IReductionActionContent,
}> = ({
  name,
  icon,
  relevance,
  savingEmission,
  hasAction,
  actionId,
  isCustom,
  description,
  categoryId,
  groupId,
  getAllActionsForOrganisation,
  setDialogOpen,
  getGroupActions,
  selectedGroup,
  getRecommendedActions,
  content,
}) => {

    const navigate = useNavigate();
    //Sercices
    const reductionActionService = new ReductionActionService()
    const reportPeriodContext = useContext(ReportPeriodContext)
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [openReductionActionDialog, setOpenReductionActionDialog] = React.useState(false);
    const [newReductionAction, setNewReductionAction] = useState<ICreateReductionAction>({
      name: name,
      description: description,
      groupId: groupId,
      categoryId: categoryId,
      icon: icon,
    })
    const [superAdminStatus, setSuperAdminStatus] = useState<boolean>(false)
    //Loading State
    const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)

    useEffect(() => {
      setNewReductionAction({
        name: name,
        description: description,
        groupId: groupId,
        categoryId: categoryId,
        icon: icon,
      })
    }, [name, description, groupId, categoryId, icon])

    const addClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, actionId: string) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        if (reportPeriodContext.lastSelectedOpenReportId) {
          reductionActionService.addReductionActionToReportPeriod({
            reductionActionId: actionId,
            reportPeriodId: reportPeriodContext.lastSelectedOpenReportId
          })
            .then(() => {
              getAllActionsForOrganisation();
              userNotificationsContext.openSnackbar({ open: true, message: 'Added successfully' })
            })
            .catch(err => console.log(err))
        }
      }
    }

    const viewClickHandler = (actionId: string) => {
      setDialogOpen(true)
      navigate(`/reduction-plan/actions/library?&actionId=${actionId}`)
    }

    const openReductionActionDialogHandler = (isSuperAdmin: boolean) => {
      setSuperAdminStatus(isSuperAdmin)
      setOpenReductionActionDialog(true)
    }

    const closeReductionActionDialogHandler = () => {
      setOpenReductionActionDialog(false);
    };

    const submitActionHandler = (values: ICreateReductionAction, resetForm: () => void, setSelectedCategoriesId: React.Dispatch<SetStateAction<any>>) => {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        reductionActionService.editReductionAction(actionId, values)
          .then(response => {
            setOpenReductionActionDialog(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            if (selectedGroup !== 'recommended') {
              getGroupActions();
            }
            if (selectedGroup === 'recommended') {
              getRecommendedActions();
            }
            getAllActionsForOrganisation();
            setSelectedCategoriesId([])
            resetForm()
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            setIsDialogSubmitting(false)
            console.log(err)
          })
      }
    }


    return (
      <Card sx={{
        height: '100%',
        boxShadow: 'none', borderWidth: '2px', borderStyle: 'solid',
        borderColor: !hasAction ? 'primary.main' : 'lightGrey.light',
        borderRadius: '10px',
      }}>
        <CardContent sx={{ height: '100%', position: 'relative' }}>
          <Stack display='flex' direction='row' justifyContent='center' alignItems='center' sx={{ width: '100%' }}>
            {userDetails?.isSuperAdmin &&
              <Button variant='outlined' onClick={() => openReductionActionDialogHandler(true)}>
                <AdminPanelSettingsOutlined />
                <Typography>Edit</Typography>
              </Button>
            }
            {isCustom &&
              <Button variant='outlined' onClick={() => openReductionActionDialogHandler(false)} sx={{ ml: 1 }}>
                <EditOutlined />
                <Typography>Edit</Typography>
              </Button>}
          </Stack>
          <Stack display='flex' direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
            {isCustom && <>
              <Chip label='Custom' />
            </>}
            <Stack display='flex' justifyContent='flex-end' alignItems='flex-end' sx={{ width: '100%' }}>
              {!hasAction && !isReportPeriodLocked &&
                <IconButton onClick={(e) => addClickHandler(e, actionId)}>
                  <Add color='primary' />
                </IconButton>}
              {hasAction &&
                <IconButton disabled>
                  <Done />
                </IconButton>}
            </Stack>
          </Stack>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 8, mb: 10 }}>
              <IconPicker icon={icon} scale={3} color={'text.secondary'} />
            </Box>
            <Divider />
            <Stack display='flex' direction='row' alignItems='center' justifyContent='space-between' sx={{ mt: 2, pr: 1, pl: 1, pt: 1, }}>
              <Stack>
                <Box onClick={!isReportPeriodLocked ? () => viewClickHandler(actionId) : undefined}>
                  <Typography variant='h6'
                    sx={{
                      textDecoration: 'none',
                      '&:hover': { textDecoration: !isReportPeriodLocked ? 'underline' : 'none' },
                      cursor: !isReportPeriodLocked ? 'pointer' : 'default'
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
                {content && <ReductionActionContentSummary action={content} variant='compact' />}
              </Stack>
              {/* <Stack>
                <Box sx={{ position: 'relative', width: 60, height: 60 }}>
                  <CircularProgress
                    variant="determinate"
                    sx={{
                      color: (theme) =>
                        theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
                    }}
                    size={60}
                    thickness={4}
                    value={100}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={relevance}
                    size={60}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      sx={{ fontWeight: 600, fontSize: '1rem' }}
                    >
                      {`${relevance}%`}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant='subtitle1' sx={{ color: 'text.secondary', fontSize: '0.7rem', textAlign: 'center' }}>Relevance</Typography>
            </Stack> */}
            </Stack>
          </Box>
        </CardContent>
        {openReductionActionDialog &&
          <AddCustomReductionActionDialog
            isEdit={true}
            isSubmitting={isDialogSubmitting}
            newReductionAction={newReductionAction}
            isSuperAdmin={superAdminStatus}
            open={openReductionActionDialog}
            closeHandler={closeReductionActionDialogHandler}
            submitHandler={submitActionHandler} />}
      </Card >
    )
  }